import { FC, memo, useRef, useEffect, useMemo } from "react";
import { Pressable, StyleProp, ViewStyle, Platform, View } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useCellValue, useCellDragged, type GridStore } from "../Grids/grid-store";
import { CELL_MARGIN_BOTTOM } from "../Grids/constants";

interface DraggableDayCellProps {
  day: number | string;
  hour: number;
  onPress: (day: number | string, hour: number) => void;
  registerCell: (day: number | string, hour: number, ref: any) => void;
  store: GridStore;
  style?: StyleProp<ViewStyle>;
}

export const DraggableDayCell: FC<DraggableDayCellProps> = memo(
  ({ day, hour, onPress, registerCell, store, style }) => {
    const { theme } = useUnistyles();
    const isActive = useCellValue(store, day, hour);
    const isDragged = useCellDragged(store, day, hour);
    const webRef = useRef<HTMLDivElement>(null);

    const dynamicStyles = useMemo<ViewStyle>(() => {
      const armedColor = theme.colors.alarmStates.armedSecondary as string;
      const disarmedBackground = theme.colors.background as string;
      const draggedBackground = theme.colors.interactive.controls.disabled_background as string;
      const borderColorBase = theme.colors.borderCard as string;
      const borderColorSecondary = theme.colors.alarmStates.armedBorderSecondary as string;

      const backgroundColor =
        isDragged ? draggedBackground
        : isActive ? armedColor
        : disarmedBackground;

      const borderColor =
        isDragged ? draggedBackground
        : isActive ? borderColorSecondary
        : borderColorBase;

      return {
        backgroundColor,
        borderColor,
        opacity: isDragged ? 0.7 : 1,
      };
    }, [isDragged, isActive, theme]);

    // Register the cell with the appropriate ref based on platform
    useEffect(() => {
      if (Platform.OS === "web" && webRef.current) {
        registerCell(day, hour, webRef.current as any);
        return () => registerCell(day, hour, null);
      }
    }, [day, hour, registerCell]);

    const handlePress = () => {
      if (Platform.OS === "web") {
        // Block press if currently dragging or drag just ended
        const { isDragging, dragEndedAt } = store.getState();
        if (isDragging || Date.now() - dragEndedAt < 150) return;
      }
      onPress(day, hour);
    };

    return (
      <Pressable onPress={handlePress} style={[styles.container, style]}>
        <View
          ref={(ref) => {
            if (Platform.OS === "web") {
              // @ts-expect-error - Web uses HTMLElement
              webRef.current = ref;
            } else {
              registerCell(day, hour, ref);
            }
          }}
          style={[styles.cell, dynamicStyles]}
        />
      </Pressable>
    );
  },
  (prevProps, nextProps) => {
    // isActive and isDragged are read from the store (useStore), so
    // store-triggered re-renders bypass memo. This comparator only gates
    // re-renders caused by PARENT re-renders — which should never happen
    // now that all volatile props have been moved to the store.
    return prevProps.day === nextProps.day && prevProps.hour === nextProps.hour;
  },
);

DraggableDayCell.displayName = "DraggableDayCell";

const styles = StyleSheet.create((theme) => ({
  container: {
    flex: 1,
    marginHorizontal: CELL_MARGIN_BOTTOM / 2,
    marginVertical: Platform.OS === "web" ? 30 : 0,
    top: Platform.OS === "web" ? -30 : 0,
  },
  cell: {
    height: 30,
    borderWidth: 1,
  },
}));
